<template>
  <fin-data-loader ref="backtesting" v-resize="reflowChart" :resolver="loadData" :params="portfolioData.id">
    <template #content>
      <v-row>
        <v-col cols="12" lg="4" md="5" order="2" order-sm="1" sm="7">
          <fin-portfolio-value v-style:display="$vuetify.breakpoint.smAndUp ? 'inline-block' : ''"
                               :hide-hint="false"
                               :hint="hint"
                               :title="'Simulierter Wert (' + years + ' Jahre)'">
            <template #valueLine>
              <span :style="{color: simValue < 0 ? $finColors.accentRed : $finColors.accentGreen}">
                {{ simValue | finCurrency | isNaN }}
              </span>
            </template>

            <template #valueSubline>
              <span :style="{color: backtrackedPerformance < 0 ? $finColors.accentRed : $finColors.accentGreen}">
                {{ valueSubline }}
              </span>
            </template>
          </fin-portfolio-value>
        </v-col>
        <v-col class="text-right" order="1" order-sm="2">
          <v-btn-toggle v-model="years"
                        class="backtrack-year-buttons"
                        mandatory
                        rounded>
            <v-btn :dark="years === 3"
                   :value="3">
              <span :style="{ color: years === 3 ? $finColors.primaryLight : $finColors.primaryDark }">3J</span>
            </v-btn>
            <v-btn :dark="years === 5"
                   :value="5">
              <span :style="{ color: years === 5? $finColors.primaryLight : $finColors.primaryDark }">5J</span>
            </v-btn>
            <v-btn :dark="years === 10"
                   :value="10">
              <span :style="{ color: years === 10 ? $finColors.primaryLight : $finColors.primaryDark }">10J</span>
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <fin-data-table :headers="tableHeaders"
                      :resolver="instruments"
                      class="my-10"
                      fixed-header/>

      <fin-big-performance-chart ref="backtrackingChart"
                                 hidePercentageChange
                                 :chartOptions="chartOptions"/>
    </template>
  </fin-data-loader>
</template>

<script>
import FinDataLoader from "@/components/common/DataLoader"
import {finPortfolioAPI} from "@/js/finAPI"
import FinDataTable from "@/components/common/DataTable/FinDataTable"
import {TablePresets} from "@/components/common/DataTable/DefaultHeaders"
import FinChartBuilder from "@/components/common/Chart2/FinChartBuilder"
import {DEFAULT_CHART_OPTIONS} from "@/components/common/Chart2/HighChartInit"
import FinPortfolioValue from "@/components/pages/myPortfolio/PortfolioValue"
import FinBigPerformanceChart from "@/components/common/Chart2/PerformanceCharts/BigPerformanceChart"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {selectCompareExtreme, updateRebaseButton} from "@/components/common/Chart2/CompareChart"

const queryCompare = {
  SHARE: `
    query($id:ID!){
      instrument: getShare(id:$id){
          id
          mainListing{
            timeSeries {
              x:timestamp
              y:close
            }
          }
      }
    }
  `,
  INDEX:`
    query($id:ID!){
      instrument: getIndex(id:$id){
          id
          mainListing{
            timeSeries {
              x:timestamp
              y:close
            }
          }
      }
    }
  `,
  ETF:`
    query($id:ID!){
      instrument: getEtf(id:$id){
          id
          mainListing{
            timeSeries {
              x:timestamp
              y:close
            }
          }
      }
    }
  `,
  PORTFOLIO: `
    query($id: ID!, $years: Int!) {
      portfolio: getBacktrackedPortfolio(portfolioId: $id, years: $years) {
        id
        name
        timeSeries {
          y:yield
          x:timestamp
        }
      }
    }
  `
}


const seriesId = (id)=> 'series_'+id

export default {
  name: "FinPortfolioBacktesting",
  components: {FinBigPerformanceChart, FinPortfolioValue, FinDataTable, FinDataLoader},
  props: {
    portfolioData: {
      type: Object
    },
  },
  data() {
    return {
      years: 3,
      instruments: [],
      timeSeries: [],
      hint: 'Wertentwicklung seit simuliertem "Kauf"',
    }
  },
  computed: {
    tableHeaders() {
      return TablePresets.createPortfolioBacktrackingDataTableHeaders()
    },
    chartOptions() {
      if (this.instruments.length > 0) {

        return [
          {
            title: 'Performance',
            chart: () => {
              const seriesData = FinChartBuilder.mapTimeSeriesPoint(this.timeSeries, 'yield', 'timestamp', false)
              seriesData.forEach( point => point[1] = point[1] + 100)

              const chart = FinChartBuilder.createCardSelectorChart('Performance über ' + this.years + ' Jahre', seriesData, 'big', this.$vuetify.breakpoint, false, '%',
                  undefined, true, true, undefined, DEFAULT_CHART_OPTIONS.rangeSelector.buttons.findIndex(r => r.type === "all"))
              chart.series[0].id=seriesId(this.portfolioData.id)
              chart.currentValueTitle = 'Performance seit Start'
              chart.prependPerformanceText = 'Performance seit'
              chart.tooltip.pointFormat = `<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.change}%</b>`
              chart.currentValue = (val) => val - 100
              chart.plotOptions = {
                series: {
                  compare: 'percent',
                },
              }
              chart.xAxis = {
                events: {
                  afterSetExtremes: function (ev) {
                    let chart = this.chart
                    if (chart) {
                      updateRebaseButton(chart)
                    }
                  }
                }
              }

              async function getSeries(userId, chart, selected, years) {
                const {data} = await API.graphql(graphqlOperation(queryCompare[selected.type], {
                  id: selected.item.id,
                  years
                }))
                const isPotifolio = selected.type === 'PORTFOLIO'
                const timeseries = isPotifolio ? data.portfolio.timeSeries : data.instrument.mainListing.timeSeries
                if (isPotifolio) {
                  timeseries.forEach(item => item.y = item.y + 100)
                }
                const seriesdata = FinChartBuilder.mapTimeSeriesPoint(timeseries, 'y', 'x')
                const series = FinChartBuilder.createCardSelectorSeries(selected.item.name + (isPotifolio ? '' : ` (${selected.item.id})`), seriesdata, null)
                series.fillColor = {} //disable gradient
                series.id=seriesId(selected.item.id)
                series.color = selected.color

                return series
              }

              chart.finCompare = {
                keepSelection: true,
                select: async (chart, selected) => {
                  chart = this.$refs.backtrackingChart?.$refs?.chart.chart
                  console.log(chart, this.$refs.backtrackingChart?.$refs?.chart.chart, this.$refs.backtrackingChart?.$refs?.chart.chart === chart)

                  const series = await getSeries(this.userId, chart, selected, this.years)
                  if (!series) {
                    return '#ff0000'
                  }

                  const {color} = await chart.addSeries(series)

                  this.$nextTick( () => {
                    selectCompareExtreme(chart)
                  })
                  return color
                },
                selectAll: async (chart, selected) => {
                  const sereis = await Promise.all(selected.map(item => getSeries(this.userId, chart, item)))
                  sereis.forEach(item => chart.addSeries(item))
                },
                unselect: async (chart, selected) => {
                  //TODO remove subscription aswell
                  await chart.get(seriesId(selected.item.id)).remove()
                  this.$nextTick(() => {
                    selectCompareExtreme(chart)
                  })
                },
                excludePortfolioID: this.portfolioData.id
              }
              return chart
            }
          },
          {
            title: 'Wertentwicklung',
            chart: () =>{
              const seriesData = FinChartBuilder.mapTimeSeriesPoint(this.timeSeries, 'currentValue', 'timestamp', false)

              const chart = FinChartBuilder.createCardSelectorChart('Wertentwicklung über ' + this.years + ' Jahre', seriesData, 'big', this.$vuetify.breakpoint, false, '€',
                  undefined, true, true, undefined, DEFAULT_CHART_OPTIONS.rangeSelector.buttons.findIndex(r => r.type === "all"))
              chart.tooltip.pointFormat = `<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y}</b>`
              chart.plotOptions= {
                series: {
                  compare: 'none',
                }
              }

              return chart
            }
          }
        ]
      } else {
        return {}
      }
    },
    backtrackedPerformance() {
      return this.timeSeries.length === 0 ? 0 : this.timeSeries[this.timeSeries.length - 1].result
    },
    backtrackedPercentage() {
      return this.timeSeries.length === 0 ? 0 : this.timeSeries[this.timeSeries.length - 1].yield
    },
    valueSubline() {
      if (this.timeSeries.length === 0) {
        return 0
      } else {
        return this.$options.filters.currencyChange(this.backtrackedPerformance) + ' (' + this.$options.filters.percent(this.backtrackedPercentage) + ')'
      }
    },
    simValue() {
      if (this.instruments.length === 0) {
        return 0
      } else {
        let sum = 0

        this.instruments.forEach(i => {
          if (i.instrument.latestPrice) {
            sum += i.count * i.instrument.latestPrice.price
          } else {
            sum += i.count * i.instrument.close
          }
        })

        if (this.timeSeries.length > 0) {
          sum += this.timeSeries[this.timeSeries.length - 1].dividend
        }

        return sum
      }
    },
  },
  methods: {
    async loadData() {
      const backtrackingResult = await finPortfolioAPI.backtrack(this.portfolioData.id, this.years)

      if (backtrackingResult) {
        let instr = []
        if (backtrackingResult.etfEntries) {
          instr = [...backtrackingResult.etfEntries.items]
        }
        if (backtrackingResult.shareEntries) {
          instr = [...instr, ...backtrackingResult.shareEntries.items]
        }
        instr = instr.sort((a, b) => a.instrument.name.localeCompare(b.instrument.name))
        this.instruments = instr
        this.timeSeries = backtrackingResult.timeSeries
      }
    },
    reflowChart() {
      if (this.$refs.backtrackingChart?.$refs?.chart) {
        this.$refs.backtrackingChart.$refs.chart.reflow()
      }
    },
  },
  watch: {
    years() {
      this.$refs.backtesting._fetchData()
    },
  },
}
</script>

<style lang="scss" scoped>
h2 {
  font-size: 1.7em;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;

  @media only screen and (max-width: 600px) {
    font-size: 1.3em;
  }
}

h3 {
  font-size: 1.2em;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;

  @media only screen and (max-width: 600px) {
    font-size: 1em;
  }
}

.backtrack-year-buttons .v-btn:not(.v-btn--round):first-child {
  border-top-left-radius: inherit !important;
  border-bottom-left-radius: inherit !important;
}

.backtrack-year-buttons .v-btn:not(.v-btn--round):last-child {
  border-top-right-radius: inherit !important;
  border-bottom-right-radius: inherit !important;
}
</style>
